import debounce from "lodash.debounce";
import { runInAction } from "mobx";
import { useContext, useEffect, useRef } from "react";
import { useFormState } from "react-final-form";

import { clone } from "@bps/common-utils";
import { DateTime } from "@bps/date-time";
import { RequestError, ServerError } from "@bps/http-client";
import { TodaysNotesHeading } from "@libs/gateways/clinical/ClinicalGateway.dtos.ts";
import { EncounterFormValues } from "@shared-types/clinical/encounter-values.interface.ts";
import { useStores } from "@stores/hooks/useStores.ts";

import { EncounterFormContext } from "../clinical-form/models/EncounterFormContext.ts";

export const useTodaysNotesDebounce = () => {
  const { clinical, notification } = useStores();
  const { clinicalRecord, saveEncounter } = useContext(EncounterFormContext);
  const { values } = useFormState<EncounterFormValues>();

  const mainFreeText = useRef(clinicalRecord.mainFreeText);
  const notesHeadings = useRef(clone(clinicalRecord.notesHeadings));

  const sendClinicalNoteUpdate = useRef(
    debounce(async () => {
      if (!clinicalRecord.openEncounter) {
        await saveEncounter(values);
      }

      const notesData = clinicalRecord.toClinicalNoteRequest(
        mainFreeText.current,
        notesHeadings.current
      );

      if (!clinicalRecord.openEncounter) {
        notification.error("Notes unable to be saved");
        throw new Error("Cannot get encounter");
      }

      runInAction(() => {
        clinicalRecord.saving = true;
      });

      try {
        const clinicalNote = await clinical.updateEncounterClinicalNote(
          notesData,
          clinicalRecord.openEncounter.id
        );

        clinicalRecord.setEncounterNotes(clinicalNote);
        runInAction(() => {
          clinicalRecord.lastSaved = DateTime.jsDateNow();
          clinicalRecord.saving = false;
        });
      } catch (error) {
        if (error instanceof RequestError || error instanceof ServerError) {
          notification.error(error, {
            messageOverride:
              "An error occurred updating the clinical note due to a network error."
          });

          // retry (after debounce)
          sendClinicalNoteUpdate.current();
        } else {
          notification.error(error, {
            messageOverride: "An error occurred updating the clinical note."
          });

          throw error;
        }
      }
    }, 1000)
  );

  useEffect(() => {
    const debounceFunction = sendClinicalNoteUpdate.current;
    return () => {
      // If navigating away, immediately execute any outstanding API requests
      debounceFunction.flush();
    };
  }, [sendClinicalNoteUpdate]);

  const updateMainFreeText = (value: string) => {
    mainFreeText.current = value;

    sendClinicalNoteUpdate.current();
  };

  const updateNotesHeading = (heading: TodaysNotesHeading, value: string) => {
    const noteToUpdate = notesHeadings.current.find(
      x => x.code === heading.code
    );
    if (!noteToUpdate) {
      return;
    }

    noteToUpdate.freeText = value;

    sendClinicalNoteUpdate.current();
  };

  return { updateMainFreeText, updateNotesHeading };
};
